import {DependabotIcon} from '@primer/octicons-react'
import {VisuallyHidden} from '@primer/react'
import type {ReactNode, SyntheticEvent} from 'react'
import {useCallback, useEffect, useLayoutEffect, useRef} from 'react'
import {AnimatedCollapse} from '../../AnimatedCollapse'
import {useSessionLogsContext} from '../Context'
import {NewToolHeader} from '../tool/NewToolHeader'
import styles from './SubagentSessionGroup.module.css'

export interface SubagentSessionGroupProps {
  title: string
  open: boolean
  pending?: boolean
  statusText?: string
  prompt?: ReactNode
  onOpenChange: (open: boolean) => void
  children: ReactNode
}

export function SubagentSessionGroup({
  title,
  open,
  pending = false,
  statusText,
  prompt,
  onOpenChange,
  children,
}: SubagentSessionGroupProps) {
  const {variant} = useSessionLogsContext()
  const instanceRef = useRef({})
  const groupRef = useRef<HTMLElement>(null)
  const attachDetails = useCallback((node: HTMLDetailsElement | null) => {
    groupRef.current = node
  }, [])
  const attachDiv = useCallback((node: HTMLDivElement | null) => {
    groupRef.current = node
  }, [])
  const focusDisclosureIfNeeded = useCallback(() => {
    const group = groupRef.current
    const activeElement = document.activeElement
    if (!group || !(activeElement instanceof HTMLElement) || !group.contains(activeElement)) return
    const disclosure = group.querySelector<HTMLElement>('summary, button')
    if (activeElement !== disclosure) disclosure?.focus()
  }, [])
  const requestOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) focusDisclosureIfNeeded()
      onOpenChange(nextOpen)
    },
    [focusDisclosureIfNeeded, onOpenChange],
  )

  useEffect(() => {
    const handleGlobalToggle = (event: Event) => {
      const customEvent = event as CustomEvent<{expand: boolean; excludeInstance: object}>
      if (customEvent.detail.excludeInstance !== instanceRef.current) {
        requestOpenChange(customEvent.detail.expand)
      }
    }

    document.addEventListener('mission-control-tool-global-toggle', handleGlobalToggle)
    return () => document.removeEventListener('mission-control-tool-global-toggle', handleGlobalToggle)
  }, [requestOpenChange])

  useLayoutEffect(() => {
    if (!open) focusDisclosureIfNeeded()
  }, [focusDisclosureIfNeeded, open])

  const handleModifierClick = useCallback(
    (expand: boolean) => {
      requestOpenChange(expand)
      document.dispatchEvent(
        new CustomEvent('mission-control-tool-global-toggle', {
          detail: {expand, excludeInstance: instanceRef.current},
        }),
      )
    },
    [requestOpenChange],
  )

  const handleHeaderToggle = useCallback(() => requestOpenChange(!open), [open, requestOpenChange])
  const handleDetailsToggle = useCallback(
    (event: SyntheticEvent<HTMLDetailsElement>) => requestOpenChange(event.currentTarget.open),
    [requestOpenChange],
  )
  const collapsedStatusText = statusText ?? (pending ? 'Working' : undefined)

  const disclosure = (
    <>
      <NewToolHeader
        title={title}
        asSummary={variant === 'default'}
        isCollapsed={!open}
        isPending={pending}
        onModifierClick={handleModifierClick}
        onToggle={variant === 'default' ? undefined : handleHeaderToggle}
        leadingVisual={<DependabotIcon />}
        statusText={open ? undefined : collapsedStatusText}
      />
      <AnimatedCollapse open={open}>
        <div className={styles.ContentWrapper}>
          <div className={styles.ConnectorLine} aria-hidden="true" />
          <div className={styles.Content}>
            {prompt ? (
              <div className={styles.PromptBubble}>
                <VisuallyHidden>Subagent prompt</VisuallyHidden>
                {prompt}
              </div>
            ) : null}
            {children}
          </div>
        </div>
      </AnimatedCollapse>
    </>
  )

  return variant === 'default' ? (
    <details
      className={styles.SubagentSessionGroup}
      ref={attachDetails}
      open={open}
      onToggle={handleDetailsToggle}
      data-testid="subagent-chat-details"
    >
      {disclosure}
    </details>
  ) : (
    <div className={styles.SubagentSessionGroup} ref={attachDiv}>
      {disclosure}
    </div>
  )
}
